/**
 * Outlier filter controls. A collapsible card that configures the statistical
 * outlier filter shared by export and plot (`outliers` atom): a method select,
 * an explicit picker of numeric target columns (raw or derived), method-specific
 * parameters, and a drop-rows / blank-values toggle.
 *
 * Off by default (`method: 'none'`) - nothing is sent and no `ol=` URL param is
 * emitted until a method is chosen AND at least one column is added. Every edit
 * re-estimates and re-syncs the URL, exactly like `filter-builder`.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import {
  outliers,
  selectedShip,
  allColumns,
  derivedSignals,
  type OutlierState,
  type OutlierMethod,
} from '../../state/store.ts';
import { scheduleEstimate } from '../../state/estimate.ts';
import { syncToUrl } from '../../state/url-sync.ts';
import { plottableColumns, isNumericColumnName } from '../derived/derived-state.ts';
import '../../ui/column-combobox.ts';
import '../../ui/plain-select.ts';

interface MethodChoice {
  value: OutlierMethod;
  label: string;
}
const METHODS: MethodChoice[] = [
  { value: 'none', label: 'Off' },
  { value: 'iqr', label: 'IQR (Tukey fences)' },
  { value: 'zscore', label: 'Z-score (σ)' },
  { value: 'percentile', label: 'Percentile clip' },
  { value: 'mad', label: 'MAD (robust)' },
  { value: 'hampel', label: 'Hampel (rolling spikes)' },
];

@customElement('outlier-controls')
export class OutlierControls extends LightElement {
  @state() private collapsed = true;

  private store = new StoreController(this, [
    outliers,
    selectedShip,
    allColumns,
    derivedSignals,
  ]);

  /* ── Mutations ────────────────────────────────────────── */

  private patch(next: Partial<OutlierState>): void {
    outliers.set({ ...outliers.value, ...next });
    scheduleEstimate();
    syncToUrl();
  }

  private setMethod(method: OutlierMethod): void {
    // Opening the picker for the first time auto-expands so the controls show.
    if (method !== 'none') this.collapsed = false;
    this.patch({ method });
  }

  private addColumn(name: string): void {
    if (!name || outliers.value.columns.includes(name)) return;
    this.patch({ columns: [...outliers.value.columns, name] });
  }

  private removeColumn(name: string): void {
    this.patch({ columns: outliers.value.columns.filter((c) => c !== name) });
  }

  private setNum(key: 'k' | 'nSigma' | 'pLow' | 'pHigh' | 'window', e: Event): void {
    const v = Number((e.target as HTMLInputElement).value);
    if (!Number.isFinite(v)) return;
    this.patch({ [key]: v } as Partial<OutlierState>);
  }

  /* ── Rendering ────────────────────────────────────────── */

  /** Numeric columns eligible as outlier targets (raw + derived pseudo-columns). */
  private numericColumns() {
    return plottableColumns().filter((c) => isNumericColumnName(c.name));
  }

  private renderParams(o: OutlierState): TemplateResult {
    if (o.method === 'iqr') {
      return html`<label class="outlier-param">
        <span>Fence ×IQR</span>
        <input type="number" min="0.1" step="0.1" .value=${String(o.k)}
          @change=${(e: Event) => this.setNum('k', e)} />
        <small>1.5 mild · 3 extreme</small>
      </label>`;
    }
    if (o.method === 'zscore' || o.method === 'mad') {
      return html`<label class="outlier-param">
        <span>± σ</span>
        <input type="number" min="0.1" step="0.1" .value=${String(o.nSigma)}
          @change=${(e: Event) => this.setNum('nSigma', e)} />
        <small>${o.method === 'mad' ? 'robust (median/MAD)' : 'from the mean'}</small>
      </label>`;
    }
    if (o.method === 'hampel') {
      return html`
        <label class="outlier-param">
          <span>± σ</span>
          <input type="number" min="0.1" step="0.1" .value=${String(o.nSigma)}
            @change=${(e: Event) => this.setNum('nSigma', e)} />
          <small>robust (rolling median/MAD)</small>
        </label>
        <label class="outlier-param">
          <span>Window ±</span>
          <input type="number" min="1" max="500" step="1" .value=${String(o.window)}
            @change=${(e: Event) => this.setNum('window', e)} />
          <small>samples each side</small>
        </label>`;
    }
    // percentile
    return html`
      <label class="outlier-param">
        <span>Lower %</span>
        <input type="number" min="0" max="100" step="0.5" .value=${String(o.pLow)}
          @change=${(e: Event) => this.setNum('pLow', e)} />
      </label>
      <label class="outlier-param">
        <span>Upper %</span>
        <input type="number" min="0" max="100" step="0.5" .value=${String(o.pHigh)}
          @change=${(e: Event) => this.setNum('pHigh', e)} />
      </label>`;
  }

  private renderBody(o: OutlierState): TemplateResult {
    const picked = new Set(o.columns);
    const available = this.numericColumns().filter((c) => !picked.has(c.name));
    return html`
      <div class="outlier-columns">
        <div class="outlier-chips">
          ${o.columns.length === 0
            ? html`<span class="outlier-empty">Pick the column(s) to clean…</span>`
            : o.columns.map(
                (c) => html`<span class="outlier-chip"
                  >${c}<button title="Remove" @click=${() => this.removeColumn(c)}>✕</button></span
                >`,
              )}
        </div>
        <column-combobox
          .columns=${available}
          .value=${''}
          clearOnPick
          placeholder="+ Add column…"
          .select=${(name: string) => this.addColumn(name)}
        ></column-combobox>
      </div>
      <div class="outlier-row">
        ${this.renderParams(o)}
        <div class="outlier-action" role="group" aria-label="Outlier action">
          <button
            type="button"
            class="mode-seg ${o.action === 'drop' ? 'active' : ''}"
            title="Remove rows whose value is out of range"
            @click=${() => this.patch({ action: 'drop' })}
          >
            Drop rows
          </button>
          <button
            type="button"
            class="mode-seg ${o.action === 'null' ? 'active' : ''}"
            title="Keep rows but blank the out-of-range value"
            @click=${() => this.patch({ action: 'null' })}
          >
            Blank values
          </button>
        </div>
      </div>
    `;
  }

  override render(): TemplateResult {
    const o = outliers.value;
    const active = o.method !== 'none';
    const addDisabled = !selectedShip.value || allColumns.value.length === 0;
    return html`
      <div class="card">
        <div class="section-header" @click=${() => (this.collapsed = !this.collapsed)}>
          <span class="arrow ${this.collapsed ? '' : 'open'}">▶</span>
          <span>Outlier filter</span>
          <span class="count"
            >${active && o.columns.length ? `(${o.columns.length} col${o.columns.length === 1 ? '' : 's'})` : ''}</span
          >
        </div>
        <div class="section-body ${this.collapsed ? 'collapsed' : ''}">
          <label class="outlier-method">
            <span>Method</span>
            <plain-select
              ?disabled=${addDisabled}
              .options=${METHODS}
              .value=${o.method}
              .select=${(v: string) => this.setMethod(v as OutlierMethod)}
            ></plain-select>
          </label>
          ${active ? this.renderBody(o) : nothing}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'outlier-controls': OutlierControls;
  }
}
